import * as React from "react";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type { CPGridV2Column } from "shared/components/shared/CPGridV2";
import { ValueObject } from "shared/types";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPVirtualAutoComplete from "shared/components/shared/CPVirtualAutoComplete/CPVirtualAutoComplete";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Label } from "@progress/kendo-react-labels";
import { useQueryClient } from "react-query";
import { Box, CPText } from "shared/components/base";
import { useCurrentUserQuery } from "data/user/user";
import {
  AgentSettlementSearchParams,
  AgentSettlementRecord,
  fetchAgentSettlementPdf,
  useSearchAgentSettlementsQuery,
} from "data/settlements/settlements";
import { downloadPdf } from "shared/helpers";
import useAppState from "shared/state/useAppState";
import type { CPAutoCompleteDataItem } from "shared/components/types";
import "./SearchAgentSettlementsPageStyles.scss";

interface SearchForm {
  agency: CPAutoCompleteDataItem | undefined;
  orderNumber: string;
  beginDate: Date | null;
  endDate: Date | null;
}

const EMPTY_FORM: SearchForm = {
  agency: undefined,
  orderNumber: "",
  beginDate: null,
  endDate: null,
};

const getAgencyItem = (
  agencyCode?: string,
): CPAutoCompleteDataItem | undefined =>
  agencyCode
    ? {
        label: agencyCode,
        value: { code: agencyCode },
      }
    : undefined;

const createColumns = (
  onDownload: (record: AgentSettlementRecord) => void,
): CPGridV2Column[] => [
  {
    field: "",
    title: "",
    width: 52,
    pinned: true,
    hide: false,
    sortable: false,
    renderCell: (data: ValueObject) => (
      <CPButton
        fillMode="flat"
        size="small"
        icon="download"
        title="Download PDF"
        onClick={() => onDownload(data.dataItem as AgentSettlementRecord)}
      />
    ),
  },
  { field: "payDate", title: "Pay Date", width: 130 },
  { field: "payeeID", title: "Payee ID" },
  {
    field: "settlementPay",
    title: "Settlement Pay",
    renderCell: (data: ValueObject) => {
      const value: number = data.dataItem.settlementPay;
      return (
        <CPText noWrap>
          {value != null
            ? value.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })
            : ""}
        </CPText>
      );
    },
  },
];

const SearchAgentSettlementsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { setToastMsg, setIsLoading } = useAppState();
  const { data: currentUser } = useCurrentUserQuery();
  const [form, setForm] = React.useState<SearchForm>(EMPTY_FORM);
  const [filterParams, setFilterParams] =
    React.useState<AgentSettlementSearchParams>({ take: 20, skip: 0 });

  const accessibleAgencies = React.useMemo(
    () => currentUser?.agencies?.map((agency) => agency.code) ?? [],
    [currentUser?.agencies],
  );

  const hasSingleAgency = accessibleAgencies.length === 1;

  React.useEffect(() => {
    if (hasSingleAgency) {
      const agencyCode = accessibleAgencies[0];
      setForm((prev) => ({
        ...prev,
        agency: getAgencyItem(agencyCode),
      }));
    }
  }, [hasSingleAgency, accessibleAgencies]);

  const { data: settlements, isFetching: isLoading } =
    useSearchAgentSettlementsQuery(filterParams);

  const handleDownload = React.useCallback(
    async (record: AgentSettlementRecord) => {
      setIsLoading(true);
      try {
        const result = await fetchAgentSettlementPdf(record, record.payeeID);
        if (result?.error) {
          setToastMsg(result.error, "error");
        } else {
          downloadPdf(
            result.pdfBytes,
            `${record.payeeID}ReportDate${record.payDate}`,
          );
        }
      } catch {
        setToastMsg(
          "An error occurred while downloading the settlement.",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [filterParams.agency, setToastMsg, setIsLoading],
  );

  const handleSearch = () => {
    adapter.resetPaging();
    setFilterParams({
      take: 20,
      skip: 0,
      agency: (form.agency?.value?.code as string) || undefined,
      orderNumber: form.orderNumber || undefined,
      beginDate: form.beginDate
        ? form.beginDate.toISOString().split("T")[0]
        : undefined,
      endDate: form.endDate
        ? form.endDate.toISOString().split("T")[0]
        : undefined,
    });
  };

  const handleClear = () => {
    if (hasSingleAgency) {
      setForm({
        agency: getAgencyItem(accessibleAgencies[0]),
        orderNumber: "",
        beginDate: null,
        endDate: null,
      });
    } else {
      setForm(EMPTY_FORM);
    }

    adapter.resetPaging();
    setFilterParams({ take: 20, skip: 0 });
  };

  const columns = React.useMemo(
    () => createColumns(handleDownload),
    [handleDownload],
  );

  const adapter = useCPGridV2({
    columns,
    data: (settlements?.records ?? []) as AgentSettlementRecord[],
    showLoader: isLoading,
    totalRecords: settlements?.totalRecords ?? 0,
  });

  const renderDrawer = React.useCallback(
    () => (
      <div className="cpgv2-filters">
        <div className="cpgv2-filters-title-bar">
          <span className="cpgv2-filters-title-bar__text">Search By</span>
        </div>

        <div className="cpgv2-filters-inputs">
          <div className="cpgv2-filter-item">
            <Label className="cpgv2-filter-label">Agency</Label>
            {hasSingleAgency ? (
              <CPTextBox
                name="agency"
                value={form.agency?.label ?? ""}
                disabled
              />
            ) : (
              <CPVirtualAutoComplete
                name="agency"
                route="settlements/agency/records/search"
                labelKey="displayLabel"
                value={form.agency}
                allowManualEntry={false}
                onChange={(item) =>
                  setForm((prev) => ({ ...prev, agency: item }))
                }
              />
            )}
          </div>

          <div className="cpgv2-filter-item">
            <Label className="cpgv2-filter-label">Order Number</Label>
            <CPTextBox
              name="orderNumber"
              value={form.orderNumber}
              placeholder="e.g. G123456"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  orderNumber: (e.value as string) ?? "",
                }))
              }
            />
          </div>

          <div className="cpgv2-filter-item">
            <Label className="cpgv2-filter-label">Begin Date</Label>
            <DatePicker
              value={form.beginDate}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, beginDate: e.value ?? null }))
              }
            />
          </div>

          <div className="cpgv2-filter-item">
            <Label className="cpgv2-filter-label">End Date</Label>
            <DatePicker
              value={form.endDate}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, endDate: e.value ?? null }))
              }
            />
          </div>
        </div>

        <div className="cpgv2-filters-actions">
          <CPButton
            themeColor="primary"
            fillMode="solid"
            size="small"
            onClick={handleSearch}
          >
            Apply
          </CPButton>
          <CPButton
            themeColor="base"
            fillMode="outline"
            size="small"
            onClick={handleClear}
          >
            Clear
          </CPButton>
        </div>
      </div>
    ),
    [form, handleClear, handleSearch, hasSingleAgency],
  );

  return (
    <Box
      className="search-agent-settlements-page"
      flexDirection="column"
      style={{ height: "100%", padding: 0 }}
    >
      <Box className="sasp-grid" style={{ padding: 0 }}>
        <CPGridV2
          name="agent-settlements-grid"
          title="Agent Settlement Results"
          adapter={adapter}
          renderDrawer={renderDrawer}
          defaultDrawerOpen
          compact
          sortable
          pageable
          noWrap
          gridLines="rows"
          // renderDrawer bypasses CPGridV2's built-in Apply/Clear wiring, so
          // page/page-size/sort changes must be merged into filterParams here
          // to trigger a refetch (the `filters` arg is ignored — this page's
          // own search form already owns the non-paging filter fields).
          onApplyFilters={(_filters, paging) => {
            setFilterParams((prev) => ({ ...prev, ...paging }));
          }}
          onRefresh={() => queryClient.invalidateQueries(["agent-settlements"])}
        />
      </Box>
    </Box>
  );
};

export default SearchAgentSettlementsPage;
